/**
 * SEO Enhancement Composable for HYS v5 Healthcare Platform
 * Provides comprehensive SEO functionality including structured data, meta tags, and LLM optimization
 * 
 * @fileoverview Advanced SEO management with healthcare-specific structured data
 * @version 1.0.0
 * @author HYS Development Team
 */

// Note: Keep imports minimal; avoid unused ones to satisfy linter

// SEO configuration interfaces
export interface SEOConfig {
  readonly title: string
  readonly description: string
  readonly keywords?: string[]
  readonly ogImage?: string
  readonly canonicalUrl?: string
  readonly publishedTime?: string
  readonly modifiedTime?: string
  readonly author?: string
  readonly articleSection?: string
  readonly locale?: 'zh-Hans' | 'zh-Hant' | 'en-US'
}

// Healthcare-specific structured data interfaces
export interface DoctorStructuredData {
  readonly '@type': 'Physician' | 'MedicalOrganization'
  readonly name: string
  readonly alternateName?: string
  readonly description?: string
  readonly image?: string
  readonly telephone?: string
  readonly email?: string
  readonly address?: AddressStructuredData
  readonly medicalSpecialty?: string[]
  readonly memberOf?: MedicalOrganizationData
  readonly hasCredential?: CredentialData[]
  readonly acceptsReservations?: boolean
  readonly paymentAccepted?: string[]
  readonly priceRange?: string
  readonly availableLanguage?: string[]
  readonly hasOfferCatalog?: {
    readonly '@type': 'OfferCatalog'
    readonly name: string
    readonly itemListElement: Array<{
      readonly '@type': 'Offer'
      readonly itemOffered: {
        readonly '@type': 'Service'
        readonly name: string
        readonly description: string
      }
    }>
  }
}

export interface AddressStructuredData {
  readonly '@type': 'PostalAddress'
  readonly streetAddress: string
  readonly addressLocality: string
  readonly addressRegion: string
  readonly postalCode: string
  readonly addressCountry: string
}

export interface MedicalOrganizationData {
  readonly '@type': 'MedicalOrganization'
  readonly name: string
  readonly telephone?: string
  readonly address?: AddressStructuredData
}

export interface CredentialData {
  readonly '@type': 'EducationalOccupationalCredential'
  readonly credentialCategory: string
  readonly recognizedBy?: string
}

// SEO error types
export interface SEOError extends Error {
  code: string
  field?: string
  details?: Record<string, unknown>
}

/**
 * Advanced SEO composable with healthcare-specific optimizations
 * @returns SEO methods and utilities
 */
const useSEO = () => {
  const { $i18n } = useNuxtApp()
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()

  /**
   * Creates a standardized SEO error
   */
  const createSEOError = (
    code: string,
    message: string,
    field?: string,
    details?: Record<string, unknown>
  ): SEOError => {
    const error = new Error(message) as SEOError
    error.code = code
    if (field) error.field = field
    if (details) error.details = details
    return error
  }

  /**
   * Validates SEO configuration
   */
  const validateSEOConfig = (config: SEOConfig): void => {
    if (!config || typeof config !== 'object') {
      throw createSEOError('INVALID_CONFIG', 'SEO configuration must be an object', 'config')
    }

    if (!config.title?.trim()) {
      throw createSEOError('MISSING_TITLE', 'SEO title is required', 'title')
    }

    if (!config.description?.trim()) {
      throw createSEOError('MISSING_DESCRIPTION', 'SEO description is required', 'description')
    }

    if (config.title.length > 60) {
      console.warn(`[useSEO] Title exceeds recommended length: ${config.title.length}/60 characters`)
    }

    if (config.description.length > 155) {
      console.warn(`[useSEO] Description exceeds recommended length: ${config.description.length}/155 characters`)
    }

    // Validate keywords array if provided
    if (config.keywords && !Array.isArray(config.keywords)) {
      console.warn('[useSEO] Keywords should be an array, converting to array')
    }
  }

  /**
   * Sets comprehensive SEO meta tags with error handling
   */
  const setSEOMeta = (config: SEOConfig): void => {
    try {
      validateSEOConfig(config)

      const locale = config.locale || $i18n.locale.value as 'zh-Hans' | 'zh-Hant' | 'en-US'
      const siteName = '北美好医生'
      // Prefer site.url from site config if present, fallback to public.siteUrl
      const rc = useRuntimeConfig() as any
      const baseUrl = (rc['site']?.['url'] as string) || (runtimeConfig.public['siteUrl'] as string) || 'https://haodrs.com'
      const fullUrl = config.canonicalUrl || `${baseUrl}${route.path}`

      // Basic meta tags
      useSeoMeta({
        title: config.title,
        description: config.description,
        keywords: config.keywords?.join(', '),
        
        // Open Graph tags
        ogTitle: config.title,
        ogDescription: config.description,
        ogImage: toAbsoluteImage(config.ogImage || '/HYS-logo-icon-06.png', baseUrl),
        ogUrl: fullUrl,
        ogType: 'website',
        ogSiteName: siteName,
        ogLocale: locale.replace('-', '_'),

        
        // Twitter Card tags
        twitterCard: 'summary_large_image',
        twitterSite: '@haodrs',
        twitterTitle: config.title,
        twitterDescription: config.description,
        twitterImage: toAbsoluteImage(config.ogImage || '/HYS-logo-icon-06.png', baseUrl),
        
        // Article-specific tags (if applicable)
        ...(config.publishedTime && { articlePublishedTime: config.publishedTime }),
        ...(config.modifiedTime && { articleModifiedTime: config.modifiedTime }),
        ...(config.author && { articleAuthor: [config.author] }),
        ...(config.articleSection && { articleSection: config.articleSection }),
        
        // Technical SEO
        robots: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
        
        // Mobile optimization
        viewport: 'width=device-width, initial-scale=1',
        themeColor: '#1f2937'
      })

      // Set canonical link and custom meta separately
      useHead({
        link: [
          { rel: 'canonical', href: fullUrl }
        ],
        meta: [
          // Healthcare-specific disclaimer
          { name: 'medical-disclaimer', content: '本网站提供的信息仅供参考，不能替代专业医疗建议。请咨询合格的医疗专业人员。' },
          // Additional Open Graph image metadata
          { property: 'og:image:width', content: '1200' },
          { property: 'og:image:height', content: '630' },
          { property: 'og:image:alt', content: config.title }
        ]
      })

      // Server-side meta for SSR
      useServerSeoMeta({
        title: config.title,
        description: config.description,
        ogTitle: config.title,
        ogDescription: config.description,
        ogImage: toAbsoluteImage(config.ogImage || '/HYS-logo-icon-06.png', baseUrl),
        robots: 'index,follow',
      })

      console.debug('[useSEO] SEO meta tags set successfully:', {
        title: config.title,
        descriptionLength: config.description.length,
        url: fullUrl
      })

    } catch (error) {
      console.error('[useSEO] Failed to set SEO meta:', error)
      throw error
    }
  }

  /**
   * Generates healthcare-specific structured data
   */
  const setDoctorStructuredData = (doctorData: DoctorStructuredData): void => {
    try {
      const rc2 = useRuntimeConfig() as any
      const baseUrl = (rc2['site']?.['url'] as string) || (runtimeConfig.public['siteUrl'] as string) || 'https://haodrs.com'
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': doctorData['@type'],
        name: doctorData.name,
        ...(doctorData.alternateName && { alternateName: doctorData.alternateName }),
        ...(doctorData.description && { description: doctorData.description }),
        ...(doctorData.image && { image: toAbsoluteImage(doctorData.image, baseUrl) }),
        url: `${baseUrl}${route.path}`,
        ...(doctorData.telephone && { telephone: doctorData.telephone }),
        ...(doctorData.email && { email: doctorData.email }),
        ...(doctorData.address && { address: doctorData.address }),
        ...(doctorData.medicalSpecialty && { medicalSpecialty: doctorData.medicalSpecialty }),
        ...(doctorData.memberOf && { memberOf: doctorData.memberOf }),
        ...(doctorData.hasCredential && { hasCredential: doctorData.hasCredential }),
        ...(doctorData.acceptsReservations !== undefined && { acceptsReservations: doctorData.acceptsReservations }),
        ...(doctorData.paymentAccepted && { paymentAccepted: doctorData.paymentAccepted }),
        ...(doctorData.priceRange && { priceRange: doctorData.priceRange }),
        ...(doctorData.availableLanguage && { availableLanguage: doctorData.availableLanguage }),
        ...(doctorData.hasOfferCatalog && { hasOfferCatalog: doctorData.hasOfferCatalog }),
        
        // Healthcare platform specific
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${runtimeConfig.public['siteUrl']}${route.path}`
        },
        
        // Accessibility and compliance
        isAccessibleForFree: true,
        conditionsOfAccess: '需要预约',
        
        // Additional healthcare metadata
        knowsAbout: doctorData.medicalSpecialty,
        serviceArea: {
          '@type': 'Place',
          name: '纽约市及周边地区'
        }
      }

      // Add structured data to head
      useHead({
        script: [
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(structuredData, null, 2)
          }
        ]
      })

      console.debug('[useSEO] Doctor structured data set successfully:', doctorData.name)

    } catch (error) {
      console.error('[useSEO] Failed to set doctor structured data:', error)
      throw createSEOError('STRUCTURED_DATA_ERROR', 'Failed to generate structured data', undefined, { error, doctorData })
    }
  }

  /**
   * Sets organization-level structured data for the healthcare platform
   */
  const setOrganizationStructuredData = (): void => {
    try {
      const organizationData = {
        '@context': 'https://schema.org',
        '@type': 'MedicalOrganization',
        name: '北美好医生',
        alternateName: 'HaoYiSheng Healthcare Platform',
        description: '北美领先的中文医疗服务平台，连接患者与优质华人医疗资源',
        url: runtimeConfig.public['siteUrl'] || 'https://haodrs.com',
        logo: {
          '@type': 'ImageObject',
          contentUrl: `${runtimeConfig.public['siteUrl'] || 'https://haodrs.com'}/HYS-logo-icon-06.png`,
          width: 512,
          height: 512
        },
        image: `${runtimeConfig.public['siteUrl'] || 'https://haodrs.com'}/HYS-logo-icon-06.png`,
        
        // Contact information
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: ['Chinese', 'English'],
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00'
          }
        },
        
        // Service area
        areaServed: {
          '@type': 'Place',
          name: '美国',
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 40.7128,
            longitude: -74.0060
          }
        },
        
        // Services offered
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: '医疗服务',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '在线医生预约',
                description: '便捷的在线医生预约服务'
              }
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '医疗信息查询',
                description: '专业的医疗信息和医生资料查询'
              }
            }
          ]
        },
        
        // Social media presence
        sameAs: [
          'https://twitter.com/haodrs',
          'https://www.facebook.com/haodrs'
        ],
        
        // Platform metadata
        foundingDate: '2020',
        slogan: '连接优质医疗资源，服务华人健康需求',
        knowsAbout: [
          '医疗预约',
          '华人医疗服务',
          '专科医生查找',
          '医疗保险咨询'
        ]
      }

      useHead({
        script: [
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(organizationData, null, 2)
          }
        ]
      })

      console.debug('[useSEO] Organization structured data set successfully')

    } catch (error) {
      console.error('[useSEO] Failed to set organization structured data:', error)
      throw createSEOError('ORG_STRUCTURED_DATA_ERROR', 'Failed to generate organization structured data', undefined, { error })
    }
  }

  /**
   * Generates LLM-optimized content metadata
   * Helps AI systems understand and index healthcare content better
   */
  const setLLMOptimizedMeta = (content: {
    contentType: 'doctor-profile' | 'article' | 'service-page' | 'search-results' | 'appointment-booking' | 'medical-specialty-directory'
    primaryTopic: string
    keyTerms: string[]
    contentSummary: string
    medicalSpecialties?: string[]
    targetAudience?: string
    services?: string[]
    conditions?: string[]
  }): void => {
    try {
      // Validate input - early return if invalid
      if (!content || typeof content !== 'object') {
        console.warn('[useSEO] Invalid LLM content provided, skipping LLM meta optimization')
        return
      }

      // Validate required fields
      if (!content.contentType?.trim()) {
        console.warn('[useSEO] Missing content type for LLM optimization')
        return
      }

      if (!content.primaryTopic?.trim()) {
        console.warn('[useSEO] Missing primary topic for LLM optimization')
        return
      }

      // Ensure arrays are valid before processing
      const keyTerms = Array.isArray(content.keyTerms) ? content.keyTerms : []
      const medicalSpecialties = Array.isArray(content.medicalSpecialties) ? content.medicalSpecialties : []
      const services = Array.isArray(content.services) ? content.services : []
      const conditions = Array.isArray(content.conditions) ? content.conditions : []

      if (keyTerms.length === 0) {
        console.warn('[useSEO] No valid key terms provided for LLM optimization')
        return
      }

      // Build base meta tags
      const metaTags = [
        // Content classification for AI systems
        { name: 'content-type', content: content.contentType },
        { name: 'primary-topic', content: content.primaryTopic },
        { name: 'key-terms', content: keyTerms.join(', ') },
        { name: 'content-summary', content: content.contentSummary || '' },
        
        // Enhanced AIGC-powered content metadata
        ...(services.length > 0 ? [{ name: 'medical-services', content: services.join(', ') }] : []),
        ...(conditions.length > 0 ? [{ name: 'treated-conditions', content: conditions.join(', ') }] : []),
        
        // Content freshness and reliability
        { name: 'content-freshness', content: new Date().toISOString() },
        { name: 'content-language', content: $i18n.locale.value },
        { name: 'content-region', content: 'US' },
        
        // AI training friendly attributes
        { name: 'ai-training-data', content: 'healthcare-information' },
        { name: 'information-quality', content: 'professional-reviewed' }
      ]

      // Add medical specialties if available
      if (medicalSpecialties.length > 0) {
        metaTags.push({
          name: 'medical-specialties',
          content: medicalSpecialties.join(', ')
        })
      }

      // Add target audience if available and not empty
      if (content.targetAudience && content.targetAudience.trim()) {
        metaTags.push({
          name: 'target-audience',
          content: content.targetAudience.trim()
        })
      }

      // LLM-specific meta tags for better content understanding
      useHead({
        meta: metaTags
      })

      console.debug('[useSEO] LLM-optimized meta set successfully:', content.contentType)

    } catch (error) {
      console.error('[useSEO] Failed to set LLM-optimized meta:', error)
      // Don't throw - allow page to continue loading
    }
  }

  /**
   * Utility to generate SEO-friendly URLs
   */
  const generateSEOUrl = (title: string, id?: string): string => {
    try {
      const slug = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens

      return id ? `${slug}-${id}` : slug
    } catch (error) {
      console.error('[useSEO] URL generation failed:', error)
      return id || 'unnamed-page'
    }
  }

  /**
   * Builds absolute image URL when given a relative path
   */
  const toAbsoluteImage = (src: string, baseUrl: string): string => {
    try {
      if (!src) return ''
      if (src.startsWith('http://') || src.startsWith('https://')) return src
      return `${baseUrl}${src.startsWith('/') ? '' : '/'}${src}`
    } catch (e) {
      return src
    }
  }

  /**
   * Emit hreflang alternates for current route for all configured locales
   */
  const setHreflangAlternates = (): void => {
    try {
      // Nuxt i18n provides path resolution via localePath, but avoid importing here to keep composable lightweight
      // Consumers can set specific alternates if needed. As a safe default, emit base homepage alternates.
      const rc3 = useRuntimeConfig() as any
      const baseUrl = (rc3['site']?.['url'] as string) || (runtimeConfig.public['siteUrl'] as string) || 'https://haodrs.com'
      const alternates = [
        { hreflang: 'zh-Hans', href: `${baseUrl}${route.path}` },
        { hreflang: 'zh-Hant', href: `${baseUrl}/zh-hant${route.path}` },
        { hreflang: 'en', href: `${baseUrl}/en${route.path}` },
        { hreflang: 'x-default', href: `${baseUrl}${route.path}` }
      ]
      useHead({ link: alternates.map(a => ({ rel: 'alternate', hreflang: a.hreflang, href: a.href })) })
    } catch {}
  }

  return {
    // Core SEO functions
    setSEOMeta,
    setDoctorStructuredData,
    setOrganizationStructuredData,
    setLLMOptimizedMeta,
    setHreflangAlternates,
    
    // Utilities
    generateSEOUrl,
    createSEOError,
    validateSEOConfig,
    
    // Types (for external use)
    // Note: TypeScript interfaces are exported at module level
  } as const
}

export default useSEO
